import format from "date-fns/format"
import isToday from "date-fns/isToday"
import isYesterday from "date-fns/isYesterday"
import PropTypes from "prop-types"
import React, { useCallback, useContext, useMemo, useState } from "react"
import { useMutation, useQueryClient } from "react-query"
import {
  CompletedActions,
  OutstandingActions,
  UnsentActions,
} from "src/actions/contract_quotes"

import Badge from "src/components/Badge"
import BoatPreviewLink from "src/components/BoatPreviewLink"
import DataTableV2 from "src/components/DataTableV2"
import DockwaAccountIndicator from "src/components/DockwaAccountIndicator"
import Tooltip from "src/components/Tooltip"

import {
  archiveContractQuote,
  expireContractQuote,
  markContractQuoteDeclined,
  printContractQuotes,
  retryContractQuote,
  sendContractQuote,
} from "src/api/Contracts"

import { useToast } from "src/hooks/use_toast"
import { useTracker } from "src/hooks/use_tracker"

import { statusBadgeColor } from "src/utils/status_badge_color_helper"

import BulkActionModal from "./BulkActionModal"
import ContractQuoteFilters from "./ContractQuoteFilters"
import { ContractGroupContext } from "./index"

const NON_SELECTABLE_STATUSES = [
  "Processing",
  "Failed",
  "Undeliverable",
  "In House Only",
]

function formatStatusDate(dateStr) {
  if (!dateStr) return null
  const date = new Date(dateStr)
  if (isToday(date)) return "Today"
  if (isYesterday(date)) return "Yesterday"
  return format(date, "MM/dd/yyyy")
}

function statusToIcon(status) {
  switch (status) {
    case "In House Only":
      return "person"
    case "Unsent":
    case "Sent":
      return "envelope-open"
    case "Boater Declined":
    case "Voided":
    case "Failed":
    case "Undeliverable":
    case "Canceled":
      return "circle-x"
    case "Completed In House":
    case "Signed":
      return "circle-check"
    default:
      return undefined
  }
}

const ContractTable = ({ table, tab }) => {
  const { canUpdate, group } = useContext(ContractGroupContext)
  const showToast = useToast()
  const tracker = useTracker()
  const queryClient = useQueryClient()
  const [isBulkActionModalOpen, setIsBulkActionModalOpen] = useState(false)

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries(["contractQuotes", group.encodedId])
  }, [queryClient, group.encodedId])

  // --- Mutations ---

  const { mutate: sendContract } = useMutation(
    (data) => sendContractQuote(data),
    {
      onSuccess: (_data, { quoteId }) => {
        invalidate()
        showToast(
          "Contracts are sending! Once it has been sent, the status will automatically update.",
          { type: "success" }
        )
        const attributes = { contract_quote_id: quoteId }
        const row = table.records.find((r) => r.encodedId === quoteId)
        if (row?.status === "Sent") attributes.resend = true
        tracker.trackEvent("contracts:send", attributes)
      },
      onError: (error) => {
        invalidate()
        showToast(
          `Contract failed to send. ${error.response || error.message}`,
          {
            type: "error",
          }
        )
      },
    }
  )

  const { mutate: expireContract } = useMutation(expireContractQuote, {
    onSuccess: (data) => {
      invalidate()
      showToast(data.message, { type: "success" })
    },
    onError: (error) => {
      invalidate()
      showToast(error.message, { type: "error" })
    },
  })

  const { mutate: retryContract } = useMutation(retryContractQuote, {
    onSuccess: (data) => {
      invalidate()
      showToast(data.message, { type: "success" })
    },
    onError: (error) => {
      invalidate()
      showToast(error.message, { type: "error" })
    },
  })

  const { mutate: markContractDeclined } = useMutation(
    markContractQuoteDeclined,
    {
      onSuccess: (data) => {
        invalidate()
        showToast(data.message, { type: "success" })
      },
      onError: (error) => {
        invalidate()
        showToast(error.message, { type: "error" })
      },
    }
  )

  const { mutate: archiveContract } = useMutation(archiveContractQuote, {
    onSuccess: (data) => {
      invalidate()
      showToast(data.message, { type: "success" })
    },
    onError: (error) => {
      invalidate()
      showToast(error.message, { type: "error" })
    },
  })

  // --- Mutation handlers ---

  const handleSend = useCallback(
    (row) =>
      sendContract({
        quoteId: row.encodedId,
        groupId: group.encodedId,
      }),
    [sendContract, group.encodedId]
  )

  const handleRetry = useCallback(
    (row) =>
      retryContract({
        quoteId: row.encodedId,
        groupId: group.encodedId,
      }),
    [retryContract, group.encodedId]
  )

  const handleExpire = useCallback(
    (row) =>
      expireContract({
        quoteId: row.encodedId,
        groupId: group.encodedId,
      }),
    [expireContract, group.encodedId]
  )

  const handleMarkDeclined = useCallback(
    (row) =>
      markContractDeclined({
        quoteId: row.encodedId,
        groupId: group.encodedId,
      }),
    [markContractDeclined, group.encodedId]
  )

  const handleArchive = useCallback(
    (row) =>
      archiveContract({
        quoteId: row.encodedId,
        groupId: group.encodedId,
      }),
    [archiveContract, group.encodedId]
  )

  const handleCompleteInHouse = useCallback((row) => {
    if (row?.completeInHouseUrl) {
      window.location.href = row.completeInHouseUrl
    }
  }, [])

  const handleBulkSend = useCallback(() => setIsBulkActionModalOpen(true), [])

  // --- Columns ---

  const columns = useMemo(
    () => [
      {
        key: "customer_name",
        header: "Name",
        sortable: true,
        cardRole: "title",
        width: "180px",
        render: (row) => (
          <div className="overflow-hidden">
            <a
              className="truncate font-semibold text-blue-600"
              href={row.contactUrl}
              target="_blank"
              rel="noreferrer"
            >
              {row.customerName}
            </a>
            {row.contactBoat && (
              <div className="text-muted truncate text-xs">
                <BoatPreviewLink boat={row.contactBoat} />
              </div>
            )}
          </div>
        ),
      },
      {
        key: "isUser",
        header: "Dockwa account",
        cardRole: "detail",
        render: (row) => <DockwaAccountIndicator linked={row.isUser} />,
      },
      {
        key: "compact_rate_text",
        header: "Rate",
        cardRole: "detail",
        render: (row) => (
          <div>
            <div>{row.compactRateText}</div>
            {!row.monthlyBilling && (
              <div className="text-muted text-xs">
                {row.formattedTotal} total
              </div>
            )}
          </div>
        ),
      },
      {
        key: "status",
        header: "Status",
        cardRole: "badge",
        render: (row) => {
          const badge = (
            <Badge
              color={statusBadgeColor(row.status)}
              text={row.status}
              icon={statusToIcon(row.status)}
              iconStyle="solid"
            />
          )

          let tooltipBadge = badge
          if (row.failureReason && row.status === "Failed") {
            tooltipBadge = (
              <Tooltip
                text={row.failureReason}
                placement="top"
                variant="dark"
                maxWidth="400px"
              >
                {badge}
              </Tooltip>
            )
          } else if (row.viewedByBoaterText && row.status === "Sent") {
            tooltipBadge = (
              <Tooltip
                text={row.viewedByBoaterText}
                placement="top"
                variant="dark"
                maxWidth="300px"
              >
                {badge}
              </Tooltip>
            )
          }

          return (
            <div>
              <div className="w-min">{tooltipBadge}</div>
              {row.statusUpdatedAt && (
                <div className="text-muted mt-1 text-xs">
                  {formatStatusDate(row.statusUpdatedAt)}
                </div>
              )}
            </div>
          )
        },
      },
    ],
    []
  )

  // --- Actions ---

  const isSelectableTab = tab !== "completed"

  const actions = useMemo(() => {
    switch (tab) {
      case "unsent":
        return UnsentActions({
          onSend: handleSend,
          onRetry: handleRetry,
          onExpire: handleExpire,
          onCompleteInHouse: handleCompleteInHouse,
          onBulkSend: handleBulkSend,
          canUpdate,
        })
      case "outstanding":
        return OutstandingActions({
          onResend: handleSend,
          onRetry: handleRetry,
          onExpire: handleExpire,
          onMarkDeclined: handleMarkDeclined,
          onCompleteInHouse: handleCompleteInHouse,
          onBulkResend: handleBulkSend,
          canUpdate,
        })
      case "completed":
        return CompletedActions({
          onArchive: handleArchive,
        })
      default:
        return []
    }
  }, [
    tab,
    canUpdate,
    handleSend,
    handleRetry,
    handleExpire,
    handleMarkDeclined,
    handleArchive,
    handleCompleteInHouse,
    handleBulkSend,
  ])

  const isRowSelectable = useCallback(
    (row) => !NON_SELECTABLE_STATUSES.includes(row.status),
    []
  )

  return (
    <div className="rounded-b border-b border-l border-r bg-white px-2 py-4">
      <ContractQuoteFilters
        table={table}
        filterOptions={table.data?.filterOptions}
      />
      <DataTableV2
        name="contract-group-table"
        columns={columns}
        data={table.records}
        actions={actions}
        hideSettings
        rowCount={15}
        emptyState={
          <span className="text-gray-500">
            No contracts match your current filters
          </span>
        }
        {...table.tableProps}
        selection={isSelectableTab ? table.selection : undefined}
        onSelectionChange={
          isSelectableTab ? table.tableProps.onSelectionChange : undefined
        }
        isRowSelectable={isSelectableTab ? isRowSelectable : undefined}
        onPrint={
          isSelectableTab
            ? (ids) => {
                const { page, ...printParams } = table.fetchParams
                return printContractQuotes({
                  groupId: group.encodedId,
                  ids,
                  ...printParams,
                })
              }
            : undefined
        }
      />
      <BulkActionModal
        isOpen={isBulkActionModalOpen}
        setIsOpen={setIsBulkActionModalOpen}
        selectedContracts={table.selection}
        action={tab === "unsent" ? "send" : "resend"}
      />
    </div>
  )
}

ContractTable.propTypes = {
  table: PropTypes.object.isRequired,
  tab: PropTypes.string.isRequired,
}

export default ContractTable
